import { useState } from "react";
import { api, useAuth, errText } from "@/lib/api";
import { LogoMark } from "@/components/Logo";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff, ShieldCheck } from "lucide-react";

export default function ForceChangePassword() {
  const { user, setUser, logout } = useAuth();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.next.length < 6) return setError("New password must be at least 6 characters");
    if (form.next !== form.confirm) return setError("Passwords do not match");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      setUser(data);
      toast.success("Password updated. Welcome to FSCD Global Skills!");
    } catch (err) {
      setError(errText(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const input = "w-full h-12 px-4 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-600";

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-950 via-teal-900 to-slate-900 flex items-center justify-center px-4 py-10">
      <div data-testid="force-change-password" className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-7 sm:p-9 space-y-5">
        <div className="flex items-center gap-3">
          <LogoMark size={42} />
          <div>
            <h1 className="font-heading text-xl font-extrabold text-slate-900">Set your new password</h1>
            <p className="text-xs text-slate-500">Signed in as {user?.email}</p>
          </div>
        </div>
        <div className="flex items-start gap-2.5 bg-teal-50 border border-teal-100 rounded-xl p-3.5">
          <ShieldCheck size={18} className="text-teal-700 mt-0.5 shrink-0" />
          <p className="text-xs text-teal-900 leading-relaxed">Your account was created with a temporary password. For your security, choose a personal password before continuing.</p>
        </div>
        <form onSubmit={submit} className="space-y-3.5">
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600">Temporary password</label>
            <input data-testid="current-password-input" type={show ? "text" : "password"} required value={form.current}
              onChange={(e) => setForm({ ...form, current: e.target.value })} className={input} placeholder="The password you signed in with" />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600">New password</label>
            <div className="relative">
              <input data-testid="new-password-input" type={show ? "text" : "password"} required value={form.next}
                onChange={(e) => setForm({ ...form, next: e.target.value })} className={input} placeholder="At least 6 characters" />
              <button type="button" data-testid="toggle-password-visibility" onClick={() => setShow(!show)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {show ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600">Confirm new password</label>
            <input data-testid="confirm-password-input" type={show ? "text" : "password"} required value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={input} placeholder="Repeat new password" />
          </div>
          {error && <p data-testid="change-password-error" className="text-sm text-red-600">{error}</p>}
          <button data-testid="change-password-submit" type="submit" disabled={busy}
            className="w-full h-12 rounded-xl bg-teal-800 hover:bg-teal-700 text-white font-semibold text-sm inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
            <KeyRound size={16} /> {busy ? "Updating…" : "Update password & continue"}
          </button>
        </form>
        <button data-testid="change-password-logout" onClick={logout} className="w-full text-center text-xs text-slate-400 hover:text-slate-600">Sign out instead</button>
      </div>
    </div>
  );
}
